/**
 * The site's public pages: their address, <title>, meta description and heading. One list for both halves: the worker
 * (worker/pages.ts) writes them into the HTML it sends, so search engines see real text without running the site, and
 * the page sets them again as people move around (GuardianHub). No imports, so the worker can use it too.
 *
 * Everything else (a Guardian's own pages, the Item Manager) gets the plain site title and is kept out of search
 * results: signed out, those pages are only a sign-in card.
 */

export const SITE = 'D2 Companion';
export const ORIGIN = 'https://d2companion.co.za';

export interface PageInfo {
  /** The address, with ?planner=1 for the Raid Planner (it is a part of Maps). */
  path: string;
  title: string;
  description: string;
  h1: string;
}

export const PUBLIC_PAGES: PageInfo[] = [
  {
    path: '/',
    title: 'D2 Companion: Destiny 2 rotations, Xûr, item look-up and maps',
    description: "D2 Companion by MrZagga: what is on in Destiny 2 right now, this week's rotations and Xûr, any weapon or armour with god rolls, destination maps and a raid planner. Sign in with Bungie for your own vault and Guardian.",
    h1: 'D2 Companion',
  },
  {
    path: '/guardian/operations/rotations',
    title: 'Destiny 2 rotations this week: exotic mission, Wellspring, Altars, Ascendant Challenge | D2 Companion',
    description: "This week's and today's Destiny 2 rotations, and the ones coming up: the exotic mission, the Wellspring, Lucent Executioners, Altars of Sorrow and the Ascendant Challenge.",
    h1: 'Destiny 2 rotations',
  },
  {
    path: '/guardian/operations/xur',
    title: 'Where is Xûr and what is he selling this week? | D2 Companion',
    description: "Whether Xûr is in Destiny 2 right now, when he leaves or comes back, and what he sells this week, from Bungie's own vendor list.",
    h1: 'Xûr this week',
  },
  {
    path: '/guardian/operations/d2-info',
    title: 'D2 Info: Destiny 2 weapon and armour look-up with god rolls | D2 Companion',
    description: 'Look up any Destiny 2 weapon or armour piece: its perks, stats and the community god rolls, where it drops, the PvP and PvE meta, and raid and dungeon guides.',
    h1: 'D2 Info: weapon and armour look-up',
  },
  {
    path: '/guardian/maps',
    title: 'Destiny 2 destination maps: chests, region chests, lost sectors and more | D2 Companion',
    description: 'Interactive maps of every Destiny 2 destination, from the EDZ to the Pale Heart and Kepler: lost sectors, region chests, collectibles, vendors and patrol zones.',
    h1: 'Destiny 2 maps',
  },
  {
    path: '/guardian/maps?planner=1',
    title: 'Destiny 2 raid planner: encounter maps and team plans | D2 Companion',
    description: 'Plan a Destiny 2 raid with your team: the map of each encounter, who goes where, and the loot, for every current raid.',
    h1: 'Raid Planner',
  },
];

/** The public page at this address, if it is one. /guardian and /guardian/operations are the front page. */
export function publicPage(pathname: string, search = ''): PageInfo | undefined {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/guardian/maps' && new URLSearchParams(search).has('planner')) return PUBLIC_PAGES.find((p) => p.path === '/guardian/maps?planner=1');
  if (path === '/guardian' || path === '/guardian/operations' || path === '/guardian/operations/operations') return PUBLIC_PAGES[0];
  return PUBLIC_PAGES.find((p) => p.path === path);
}
